import { Menu, MenuItem } from "@mui/material";
import { MuiBox, MuiTypography, SubmitLoaderButton } from "components/atoms";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Button } from "components/ui";
import { Check, EllipsisVertical, Trash, X } from "lucide-react";

import {
  DeleteReviewsAction,
  UpdateReviewAction,
} from "store/middlewares/reviews";
import {
  openAdminModal,
  closeAdminModal,
} from "store/slices/common/adminModal.slice";
import checkAccess from "utils/checkAccess";

// One confirm modal for Accept / Reject / Delete.
// onConfirm must return the dispatch(...) promise so the loader stays on until the API is done.
const ReviewConfirmModal = ({
  Icon,
  iconClass,
  title,
  points,
  submitText,
  onConfirm,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await onConfirm();
    setLoading(false);
    // keep the modal open if the request failed, so the user can try again
    if (result?.meta?.requestStatus !== "rejected") {
      dispatch(closeAdminModal());
    }
  };

  return (
    <form onSubmit={handleSubmit} className="grid gap-5 p-1">
      {/* Title with icon */}
      <div className="flex items-center gap-3.5">
        <div
          className={`flex size-11 shrink-0 items-center justify-center rounded-full ${iconClass}`}
        >
          <Icon className="size-5" aria-hidden="true" />
        </div>
        <h2 className="text-xl font-bold text-foreground">{title}</h2>
      </div>

      {/* What happens */}
      <ul className="grid gap-2">
        {points.map((point, i) => (
          <li
            key={i}
            className="flex gap-2.5 text-sm leading-6 text-secondary-foreground"
          >
            <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-muted-foreground" />
            <span>{point}</span>
          </li>
        ))}
      </ul>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          disabled={loading}
          onClick={() => dispatch(closeAdminModal())}
        >
          Cancel
        </Button>
        <SubmitLoaderButton btnText={submitText} isDisable={loading} />
      </div>
    </form>
  );
};

const ReviewActionBar = ({ row, filter }) => {
  const dispatch = useDispatch();

  const getUserName = () => {
    return row?.first_name
      ? `${row.first_name} ${row.last_name || ""}`.trim()
      : "this user";
  };

  const openConfirm = (config) =>
    dispatch(
      openAdminModal({
        title: "",
        content: <ReviewConfirmModal {...config} />,
      }),
    );

  const statusPayload = (status) => ({
    id: row?.id,
    firstName: row?.first_name,
    lastName: row?.last_name,
    status,
    description: row?.description,
    image: row?.image,
    star: row?.star,
    filter,
  });

  const ActionMenuArray = [
    row.status !== "rejected" && {
      label: "Reject",
      icon: <X size={16} color="#475569" />,
      onClick: () =>
        openConfirm({
          Icon: X,
          iconClass: "bg-red-100 text-red-600",
          title: "Reject this review?",
          submitText: "Reject review",
          points: [
            <>
              The review by <strong>{getUserName()}</strong> will be hidden from
              public view.
            </>,
            "It will be marked as rejected in the dashboard.",
            "You can change this status at any time.",
          ],
          onConfirm: () =>
            dispatch(UpdateReviewAction(statusPayload("rejected"))),
        }),
    },
    row.status !== "accepted" && {
      label: "Accept",
      icon: <Check size={16} color="#475569" />,
      onClick: () =>
        openConfirm({
          Icon: Check,
          iconClass: "bg-blue-100 text-blue-600",
          title: "Accept this review?",
          submitText: "Accept review",
          points: [
            <>
              The review by <strong>{getUserName()}</strong> will be published
              publicly.
            </>,
            "It will be marked as accepted in the dashboard.",
            "You can change this status at any time.",
          ],
          onConfirm: () =>
            dispatch(UpdateReviewAction(statusPayload("accepted"))),
        }),
    },
    checkAccess("delete_review") && {
      label: "Delete",
      icon: <Trash size={16} color="#475569" />,
      onClick: () =>
        openConfirm({
          Icon: Trash,
          iconClass: "bg-red-100 text-red-600",
          title: "Delete this review?",
          submitText: "Delete review",
          points: [
            <>
              The review by <strong>{getUserName()}</strong> will be permanently
              deleted.
            </>,
            "This action cannot be undone.",
            "All associated data will be removed from the system.",
          ],
          onConfirm: () =>
            dispatch(DeleteReviewsAction({ id: row?.id, filter })),
        }),
    },
  ].filter(Boolean);

  // For action menu
  const [actionMenu, setActionMenu] = useState(null);
  const handleActionMenuOpen = (event) => {
    setActionMenu(event.currentTarget);
  };
  const handleActionMenuClose = () => {
    setActionMenu(null);
  };

  return (
    <MuiBox className="flex h-full items-center justify-center gap-3">
      <Button
        className="size-7"
        mode="icon"
        variant="ghost"
        onClick={handleActionMenuOpen}
      >
        <EllipsisVertical />
      </Button>
      <Menu
        anchorEl={actionMenu}
        open={Boolean(actionMenu)}
        onClose={handleActionMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          " & .MuiMenu-paper": {
            mt: "10px",
            background: "#fff",
            boxShadow:
              "0 4px 6px -1px #e2e8f0, 0 2px 4px -2px #e2e8f0 !important",
          },
        }}
      >
        {ActionMenuArray.map((option) => (
          <MenuItem
            key={option.label}
            onClick={() => {
              handleActionMenuClose();
              option.onClick();
            }}
          >
            <MuiBox sx={{ display: "flex", alignItems: "center", gap: "9px" }}>
              <MuiBox sx={{ width: "fit-content" }}>{option.icon}</MuiBox>
              <MuiTypography
                sx={{ fontWeight: "500", fontSize: "13px", color: "#475569" }}
              >
                {option.label}
              </MuiTypography>
            </MuiBox>
          </MenuItem>
        ))}
      </Menu>
    </MuiBox>
  );
};

export default ReviewActionBar;
